'use client';

import { Mic, Paperclip, Plug, Stethoscope } from 'lucide-react';
import React from 'react';
import type { AiSkillPublic } from '@ctrl-enter/types/ai-skills';
import { usePlatform } from '../contexts/platform-context';
import { cn } from '../utils/cn';
import { Icon, type IconProps } from './icons/icon';
import { LogoMark } from './icons/svg/logo-mark';

type ShortcutAction = {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  onClick: () => void;
};

type NewChatHomeProps = {
  selectedSkill?: AiSkillPublic | null;
  onOpenSkillSelector?: () => void;
  onUpload?: () => void;
  onVoiceRecord?: () => void;
  onGoToIntegrations?: () => void;
};

export const NewChatHome: React.FC<NewChatHomeProps> = ({
  selectedSkill,
  onOpenSkillSelector,
  onUpload,
  onVoiceRecord,
  onGoToIntegrations,
}) => {
  const { isSidebar } = usePlatform();

  const shortcuts: ShortcutAction[] = [];
  if (onOpenSkillSelector) {
    shortcuts.push({
      id: 'specialist',
      label: selectedSkill ? 'Change specialist' : 'Choose specialist',
      description: selectedSkill ? selectedSkill.name : 'Focus AI on a clinical domain',
      icon: Stethoscope,
      onClick: onOpenSkillSelector,
    });
  }
  if (onUpload) {
    shortcuts.push({
      id: 'attach',
      label: 'Attach files',
      description: 'Upload documents, images, or spreadsheets',
      icon: Paperclip,
      onClick: onUpload,
    });
  }
  if (onVoiceRecord) {
    shortcuts.push({
      id: 'voice',
      label: 'Record voice',
      description: 'Dictate a question or note with your microphone',
      icon: Mic,
      onClick: onVoiceRecord,
    });
  }
  if (onGoToIntegrations) {
    shortcuts.push({
      id: 'integrations',
      label: 'Integrations',
      description: 'Connect EMR and billing data sources',
      icon: Plug,
      onClick: onGoToIntegrations,
    });
  }

  const title = selectedSkill ? selectedSkill.name : 'How can I help you today?';
  const subtitle = selectedSkill
    ? selectedSkill.tagline
    : 'Ask anything about medical billing, coding, or documentation';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center min-h-full px-4',
        isSidebar ? 'py-6' : 'py-12'
      )}
    >
      <div
        className={cn(
          'flex flex-col items-center text-center w-full animate-in fade-in duration-500',
          isSidebar ? 'max-w-sm gap-4' : 'max-w-lg gap-6'
        )}
      >
        <div className="flex flex-col items-center gap-3">
          {selectedSkill ? (
            <div
              className={cn(
                'flex items-center justify-center rounded-xl bg-primary/10 text-primary',
                isSidebar ? 'w-10 h-10' : 'w-12 h-12'
              )}
            >
              <Icon
                iconType={selectedSkill.icon as IconProps['iconType']}
                size={isSidebar ? 'md' : 'lg'}
              />
            </div>
          ) : (
            <LogoMark size={isSidebar ? 28 : 36} aria-hidden />
          )}

          <div>
            <h2
              className={cn(
                'font-semibold text-foreground',
                isSidebar ? 'text-base' : 'text-xl sm:text-2xl'
              )}
            >
              {title}
            </h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">{subtitle}</p>
          </div>
        </div>

        {shortcuts.length > 0 && (
          <div
            className={cn(
              'w-full gap-2',
              isSidebar ? 'flex flex-col' : 'grid grid-cols-1 sm:grid-cols-3 gap-3'
            )}
          >
            {shortcuts.map((shortcut, index) => {
              const ShortcutIcon = shortcut.icon;
              return (
                <button
                  key={shortcut.id}
                  type="button"
                  onClick={shortcut.onClick}
                  className={cn(
                    'group flex flex-col items-start gap-1.5 text-left',
                    'rounded-xl border border-border/50 bg-bg-neutral-8',
                    'px-3 py-3 transition-colors',
                    'hover:border-primary/40 hover:bg-primary/5',
                    'active:bg-primary/10',
                    'animate-in fade-in-0 slide-in-from-bottom-2',
                    isSidebar ? 'w-full' : 'w-full'
                  )}
                  style={{
                    animationDelay: `${index * 60}ms`,
                    animationFillMode: 'backwards',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-foreground/5 text-foreground group-hover:text-primary transition-colors">
                      <ShortcutIcon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                      {shortcut.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 pl-9">
                    {shortcut.description}
                  </p>
                </button>
              );
            })}
          </div>
        )}

        <p className="text-xs text-muted-foreground/60">
          Type a message below or use a shortcut to get started
        </p>
      </div>
    </div>
  );
};
